import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useEffect, useId, useLayoutEffect, useRef, useState, type ComponentType, type CSSProperties, type MouseEvent, type ReactElement, type ReactNode } from 'react'

import { StorefrontPriceCell } from '@/components/storefronts/StorefrontPriceCell'
import { StorefrontCompactCell } from '@/components/storefronts/StorefrontCompactCell'
import { BuyerPriceCell } from '@/components/storefronts/BuyerPriceCell'
import { MarketplaceBadgeList, ValueOrReason } from '@/components/warehouse/primitives'
import { WarehouseThumbnail } from '@/components/warehouse/WarehouseThumbnail'
import { MarketDriverIcon } from '@/components/warehouse/MarketDriverIcon'
import { StorefrontMark } from '@/components/warehouse/storefronts'
import { MarketShowcasePrice } from '@/components/warehouse/MarketShowcasePrice'
import type {
  InventoryListItem,
  InventoryProblem,
  StorefrontPrice,
  StorefrontReadiness as StorefrontReadinessValue,
} from '@/lib/api/schemas/inventory.schema'
import { formatCustomerName } from '@/lib/customer-name'
import { inventoryAlertLabels, materialLabel, publicationStateLabels, stockMismatchReasonLabels, unavailableReasonText, warehouseProductStatusLabels } from '@/lib/warehouse/labels'
import {
  displayArticle,
  businessActivityOf,
  hasMovementConflict,
  lastActivityOf,
  marketplaceBadges,
  movementSourceLabel,
  productStatusOf,
  productUinOf,
  warehouseDateOnly,
} from '@/lib/warehouse/projection'
import type { ColumnDef } from '@/lib/views/columns/types'
import type { InventoryGroupRow } from '@/lib/api/schemas/inventory.schema'
import { formatRubleAmount, formatRublePerGram } from '@/lib/market-money'
import { EM_DASH, STATUS_LABELS, SUBSTATUS_LABELS, substatusLabel } from '@/lib/order-helpers'
import { DRIFT_LABELS, YM_PRICE_LABELS } from '@/lib/ymarket-pricing'
import { ymarketDriftOf } from '@/lib/warehouse/ymarket-storefront'
import { syncText, ymarketShowcaseFigure } from '@/lib/warehouse/ymarket-showcase'
import { insertStonesText } from '@/lib/warehouse/composition'

export type InventoryColumnId =
  | 'photo'
  | 'title'
  | 'product'
  | 'attributes'
  | 'size'
  | 'weight'
  | 'metalWeight'
  | 'insertWeight'
  | 'inserts'
  | 'article'
  | 'uin'
  | 'arrivedAt'
  | 'marketplaces'
  | 'storefronts'
  | 'status'
  | 'activity'
  | 'alerts'
  | 'problems'
  | 'photoCount'
  | 'videoCount'
  | 'onecPrice'
  | 'wildberriesPrice'
  | 'ymarketPrice'
  | 'ozonPrice'
  | 'oriSitePrice'
  | 'ymarketSellsFor'
  | 'ymarketShowcase'
  | 'ymarketShowcasePerGram'
  | 'ymarketShowcaseThresholdRatio'
  | 'ymarketPromo'
  | 'ymarketBoost'
  | 'ymarketRubPerGram'
  | 'ymarketPurchaseCost'
  | 'ymarketEarnings'
  | 'ymarketEarningsPercent'
  | 'ymarketNetPerGram'
  | 'ymarketOurPrice'
  | 'ymarketDiscountBase'
  | 'ymarketMinimumForPromos'
  | 'ymarketMarketPrice'
  | 'ymarketDrift'
  | 'ymarketFreshness'
  | 'readiness'
  | 'orderNumber'
  | 'orderCreatedAt'
  | 'buyer'
  | 'orderStatus'
  | 'program'
  | 'city'
  | 'orderAmount'
  | 'composition'

export type InventoryColumnFacet = 'core' | 'storefrontPrices' | 'presence' | 'readiness' | 'ymarketEconomics' | 'order'
export type InventoryColumnDef = ColumnDef<InventoryListItem, InventoryColumnId, InventoryColumnFacet> & {
  groupCell?: (row: InventoryGroupRow) => ReactNode
}

function dash(value: string | number | null | undefined, reason: string | null | undefined) {
  return <ValueOrReason value={value === undefined ? null : value} reason={reason ?? null} />
}

function insertWeightCell(row: InventoryListItem) {
  const composition = row.composition
  if (composition === null) return dash(null, 'insert_weight.not_extracted')
  if (composition.insertWeightGrams === null) return dash(null, composition.insertWeightUnavailableReason)
  const carats = composition.insertWeightCarats === null ? '' : ` · ${composition.insertWeightCarats} кт`
  return <span className="whitespace-nowrap tabular-nums">{composition.insertWeightGrams} г{carats}</span>
}

function insertsCell(row: InventoryListItem) {
  const stones = row.composition?.insertStones ?? null
  if (stones === null) return dash(null, row.composition?.insertStonesUnavailableReason ?? 'insert_stones.not_extracted')
  return <span className="text-xs">{insertStonesText(stones)}</span>
}

function title(row: InventoryListItem) {
  return row.title ?? <ValueOrReason value={null} reason={row.titleUnavailableReason ?? 'title.not_recorded'} />
}

/** The platform preset's composite product column keeps the evidence together. */
function product(row: InventoryListItem) {
  return <span className="flex min-w-[210px] flex-col"><span className="font-semibold">{row.title ?? 'Название не записано'}</span><span className="text-[11px] text-ink-muted">{displayArticle(row) || 'Артикул не записан'} · {productUinOf(row) || 'УИН не записан'}</span></span>
}

function status(row: InventoryListItem) {
  const value = productStatusOf(row)
  const explanation = value.reason ? unavailableReasonText(value.reason) : undefined
  if (value.status === 'unknown') return <span data-product-status={value.status} data-unavailable="true" data-reason={value.reason ?? undefined} title={explanation} className="text-ink-muted">—</span>
  return <span data-product-status={value.status} data-reason={value.reason ?? undefined} title={explanation} className="inline-flex min-w-max items-center whitespace-nowrap rounded-full bg-bone px-2 py-1">{warehouseProductStatusLabels[value.status]}</span>
}

function activity(row: InventoryListItem) {
  const projected = lastActivityOf(row)
  // `lastActivityOf` preserves the public projection fallback for detail
  // consumers; the table's movement column must never relabel first capture as
  // business movement.
  const value = projected.source === 'Впервые в 1С' ? businessActivityOf(row) : projected
  const sortValue = value.at === null ? '' : value.at
  const reason = value.unavailableReason ?? productStatusOf(row).reason ?? 'inventory.activity_not_recorded'
  if (value.at === null) return <span data-activity-sort={sortValue}><ValueOrReason value={null} reason={reason} /></span>
  const source = hasMovementConflict(row) ? null : movementSourceLabel(row) ?? value.source
  return <span className="flex flex-col" data-last-movement={value.at} data-activity-sort={sortValue}><time dateTime={value.at}>{warehouseDateOnly(value.at)}</time>{source ? <span className="text-[10px] font-normal text-ink-muted">{source}</span> : null}{hasMovementConflict(row) ? <span className="text-[10px] font-normal text-ink-muted" data-reason={reason}>{unavailableReasonText(reason)}</span> : null}</span>
}

type AlertSeverity = 'error' | 'warning' | 'info'

export type AlertIconProps = { className: string }
export type AlertIconComponent = ComponentType<AlertIconProps>

export type AlertConditionDeclaration = {
  readonly id: string
  readonly reasonCode: string
  readonly wording: string
  readonly severity: AlertSeverity
  readonly icon: AlertIconComponent
  readonly matches: (row: InventoryListItem) => boolean
}

function StockMismatchIcon({ className }: AlertIconProps): ReactElement {
  return <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m12 3 9 17H3L12 3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
}

function StaleStockIcon({ className }: AlertIconProps): ReactElement {
  return <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3 2" /></svg>
}

function UnobservedStockIcon({ className }: AlertIconProps): ReactElement {
  return <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="8.5" /><path d="M9.75 9.5a2.35 2.35 0 1 1 3.65 1.97c-.9.58-1.4 1-1.4 2.03" /><path d="M12 17h.01" /></svg>
}

function DriftedPublicationIcon({ className }: AlertIconProps): ReactElement {
  return <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m9.5 14.5-1.25 1.25a3.18 3.18 0 0 1-4.5-4.5L7 8a3.18 3.18 0 0 1 4.5 0" /><path d="m14.5 9.5 1.25-1.25a3.18 3.18 0 0 1 4.5 4.5L17 16a3.18 3.18 0 0 1-4.5 0" /><path d="m8 16 8-8" /></svg>
}

function MultipleListingsIcon({ className }: AlertIconProps): ReactElement {
  return <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="4" y="4" width="12" height="12" rx="2" /><path d="M8 20h8a4 4 0 0 0 4-4V8" /></svg>
}

const ALERT_SEVERITY_CLASSES: Record<AlertSeverity, string> = {
  error: 'text-st-unpaid',
  warning: 'text-gold-deep',
  info: 'text-ink-muted',
}

function activeMarketplaceSources(row: InventoryListItem) {
  return row.sources.filter((source) => source.sourceKind === 'marketplace' && source.isActive)
}

function hasMeasuredStockMismatch(row: InventoryListItem): boolean {
  const activeAccounts = new Set(activeMarketplaceSources(row).map((source) => source.accountCode))
  return (row.stockDisagreements ?? []).some((disagreement) => activeAccounts.has(disagreement.accountCode) && disagreement.canonicalQuantity !== null && disagreement.observedQuantity !== null)
}

function hasStaleMarketplaceStock(row: InventoryListItem): boolean {
  return activeMarketplaceSources(row).some((source) => source.freshness === 'stale')
}

function hasUnobservedMarketplaceStock(row: InventoryListItem): boolean {
  const activeAccounts = new Set(activeMarketplaceSources(row).map((source) => source.accountCode))
  return row.stockMismatchFindings.some((finding) => finding.reason === 'marketplace_never_reported' && activeAccounts.has(finding.marketplaceAccountCode))
    || activeMarketplaceSources(row).some((source) => source.freshness === 'unavailable' && source.freshnessReason === 'inventory.observation_absent')
}

function hasDriftedPublication(row: InventoryListItem): boolean {
  return activeMarketplaceSources(row).some((source) => source.listingState === 'drifted')
}

function hasMultipleLiveListings(row: InventoryListItem): boolean {
  return (row.listings ?? []).filter((listing) => listing.listingStatus?.toLocaleLowerCase('en-US') === 'live').length > 1
}

export const ALERT_CONDITION_DECLARATIONS: readonly AlertConditionDeclaration[] = [
  {
    id: 'marketplace-stock-mismatch',
    reasonCode: 'stock_divergence',
    wording: stockMismatchReasonLabels.stock_divergence,
    severity: 'error',
    icon: StockMismatchIcon,
    matches: hasMeasuredStockMismatch,
  },
  {
    id: 'marketplace-stock-stale',
    reasonCode: 'inventory.observation_stale',
    wording: unavailableReasonText('inventory.observation_stale'),
    severity: 'warning',
    icon: StaleStockIcon,
    matches: hasStaleMarketplaceStock,
  },
  {
    id: 'marketplace-stock-unobserved',
    reasonCode: 'stock.provider_observation_absent',
    wording: unavailableReasonText('stock.provider_observation_absent'),
    severity: 'info',
    icon: UnobservedStockIcon,
    matches: hasUnobservedMarketplaceStock,
  },
  {
    id: 'publication-drifted',
    reasonCode: 'publication.drifted',
    wording: publicationStateLabels.drifted,
    severity: 'warning',
    icon: DriftedPublicationIcon,
    matches: hasDriftedPublication,
  },
  {
    id: 'multiple-live-listings',
    reasonCode: 'listing.multiple_live',
    wording: inventoryAlertLabels.multipleLiveListings,
    severity: 'warning',
    icon: MultipleListingsIcon,
    matches: hasMultipleLiveListings,
  },
]

type HoveredAlert = {
  readonly condition: AlertConditionDeclaration
  readonly x: number
  readonly y: number
}

function AlertTooltip({ hovered }: { hovered: HoveredAlert }): ReactElement {
  return createPortal(
    <div
      role="tooltip"
      data-alert-tooltip="true"
      data-alert-tooltip-portal="true"
      data-alert-tooltip-anchor="cursor"
      data-alert-tooltip-x={hovered.x}
      data-alert-tooltip-y={hovered.y}
      className="pointer-events-none fixed z-50 max-w-[280px] rounded-md bg-onyx px-2.5 py-2 text-xs text-ivory shadow-lg"
      style={{ left: `${hovered.x + 12}px`, top: `${hovered.y + 12}px` }}
    >
      {hovered.condition.wording}
    </div>,
    document.body,
  )
}

export function AlertsCell({ row, declarations = ALERT_CONDITION_DECLARATIONS }: { row: InventoryListItem; declarations?: readonly AlertConditionDeclaration[] }): ReactElement {
  const [hovered, setHovered] = useState<HoveredAlert | null>(null)
  const conditions = declarations.filter((condition) => condition.matches(row))
  const show = (condition: AlertConditionDeclaration, event: MouseEvent<HTMLButtonElement>) => {
    setHovered({ condition, x: event.clientX, y: event.clientY })
  }
  const clear = (conditionId: string) => {
    setHovered((current) => current?.condition.id === conditionId ? null : current)
  }

  return <span data-testid="inventory-alerts-cell" className="inline-flex min-h-6 items-center gap-1">
    {conditions.map((condition) => {
      const Icon = condition.icon
      return <button
        key={condition.id}
        type="button"
        data-alert-icon={condition.id}
        data-alert-severity={condition.severity}
        aria-label={`Алерт: ${condition.wording}`}
        className={`inline-flex size-6 items-center justify-center rounded-full hover:bg-bone focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gold ${ALERT_SEVERITY_CLASSES[condition.severity]}`}
        onMouseEnter={(event) => show(condition, event)}
        onMouseMove={(event) => show(condition, event)}
        onMouseLeave={() => clear(condition.id)}
      >
        <Icon className="size-4" />
      </button>
    })}
    {hovered ? <AlertTooltip hovered={hovered} /> : null}
  </span>
}

function ProblemIcon(): ReactElement {
  return <span data-problems-indicator="true" aria-hidden="true" className="inline-flex size-4 items-center justify-center rounded-full border border-current text-[11px] font-bold leading-none">!</span>
}

type ProblemPortalState = { x: number; y: number }
type ProblemPortalSize = { width: number; height: number }

function problemPortalPosition(x: number, y: number, size: ProblemPortalSize): CSSProperties {
  const { width, height } = size
  return {
    left: `${Math.min(Math.max(x + 12, 8), Math.max(8, window.innerWidth - width - 8))}px`,
    top: `${Math.min(Math.max(y - height - 12, 8), Math.max(8, window.innerHeight - height - 8))}px`,
  }
}

export function ProblemsCell({
  problems,
  problemsAvailable = false,
}: {
  problems: readonly InventoryProblem[]
  problemsAvailable?: boolean
}): ReactElement {
  const [portal, setPortal] = useState<ProblemPortalState | null>(null)
  const [portalSize, setPortalSize] = useState<ProblemPortalSize | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const portalRef = useRef<HTMLDivElement>(null)
  const focused = useRef(false)
  const focusClickSuppressed = useRef(false)
  const portalId = `inventory-problems-${useId().replaceAll(':', '')}`
  const show = (x: number, y: number) => setPortal({ x, y })
  const close = () => {
    setPortal(null)
    setPortalSize(null)
  }
  const showFromButton = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (rect) show(rect.right, rect.top)
  }
  useLayoutEffect(() => {
    if (!portal || portalRef.current === null) return
    const rect = portalRef.current.getBoundingClientRect()
    setPortalSize({
      width: rect.width || 300,
      height: rect.height || 190,
    })
  }, [portal])
  useEffect(() => {
    if (!portal) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    const onViewportChange = close
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onViewportChange, true)
    window.addEventListener('resize', onViewportChange)
    return () => {
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onViewportChange, true)
      window.removeEventListener('resize', onViewportChange)
    }
  }, [portal])
  if (problems.length === 0) {
    return <span data-testid="inventory-problems-cell" data-problems-empty="true" className="text-ink-muted">—</span>
  }
  return <span data-testid="inventory-problems-cell" className="inline-flex min-h-6 items-center">
    <button
      ref={buttonRef}
      type="button"
      aria-label={`Проблемы: ${problems.length}`}
      aria-describedby={portal ? portalId : undefined}
      aria-expanded={portal !== null}
      className="inline-flex size-6 items-center justify-center rounded-full text-st-unpaid hover:bg-bone focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gold"
      onMouseEnter={(event) => show(event.clientX, event.clientY)}
      onMouseMove={(event) => { if (!focused.current) show(event.clientX, event.clientY) }}
      onMouseLeave={() => { if (!focused.current) close() }}
      onFocus={() => { focused.current = true; focusClickSuppressed.current = true; showFromButton() }}
      onBlur={() => { focused.current = false; close() }}
      onClick={(event) => {
        event.stopPropagation()
        if (focusClickSuppressed.current) {
          focusClickSuppressed.current = false
          return
        }
        if (portal) close()
        else showFromButton()
      }}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.key === 'Escape' && close()}
    >
      <ProblemIcon />
    </button>
    {portal ? createPortal(
      <div
        ref={portalRef}
        id={portalId}
        role="dialog"
        aria-label="Проблемы изделия"
        data-problems-portal="true"
        data-problems-pointer-x={portal.x}
        data-problems-pointer-y={portal.y}
        className="pointer-events-auto fixed z-[70] max-h-[190px] w-[min(300px,calc(100vw-16px))] overflow-auto rounded-md bg-onyx px-3 py-2 text-xs text-ivory shadow-lg"
        style={problemPortalPosition(portal.x, portal.y, portalSize ?? { width: 300, height: 190 })}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <ul className="space-y-1.5">
          {problems.map((problem, index) => <li key={`${problem.code}-${problem.position ?? index}`}>
            <span className="font-medium">{problem.label}</span>{problem.detail ? <span className="block text-ivory/75">{problem.detail}</span> : null}
          </li>)}
        </ul>
        {!problemsAvailable ? <p className="mt-2 border-t border-ivory/20 pt-2 text-ivory/70">Часть проверки недоступна.</p> : null}
      </div>,
      document.body,
    ) : null}
  </span>
}

function mediaCount(value: number | null | undefined, attribute: 'data-photo-count' | 'data-video-count'): ReactElement {
  return <span {...{ [attribute]: value === undefined ? 'unknown' : value }}><ValueOrReason value={value ?? null} reason={null} /></span>
}

function groupText(value: string | null | undefined): ReactNode {
  return value ? <span className="font-medium text-ink">{value}</span> : <span className="text-ink-muted">{EM_DASH}</span>
}

function groupDate(value: string | null | undefined): ReactNode {
  if (!value) return <span className="text-ink-muted">{EM_DASH}</span>
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return <span className="text-ink-muted">{EM_DASH}</span>
  return <time dateTime={value} className="whitespace-nowrap text-ink-muted">{new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(parsed)}</time>
}

function orderStatus(row: InventoryGroupRow): ReactNode {
  const label = row.orderStatus ? STATUS_LABELS[row.orderStatus] ?? row.orderStatus : EM_DASH
  const normalizedSubstatus = row.orderSubstatus?.trim() || null
  const substatus = substatusLabel(row.orderSubstatus)
  const meaning = normalizedSubstatus ? SUBSTATUS_LABELS[normalizedSubstatus]?.meaning ?? 'Подстатус не распознан' : undefined
  return <span className="flex min-w-max flex-col"><span className="font-medium">{label}</span>{substatus ? <small className="text-ink-muted" data-substatus-code={normalizedSubstatus ?? undefined} title={meaning}>{substatus}</small> : null}</span>
}

function orderComposition(row: InventoryGroupRow): ReactNode {
  const count = row.pieceCount ?? row.variantCount
  const noun = count === 1 ? 'изделие' : count >= 2 && count <= 4 ? 'изделия' : 'изделий'
  return <span className="block max-w-[320px] truncate" title={row.composition ?? undefined}>{count} {noun}: {row.composition ?? EM_DASH}</span>
}

function orderColumn(
  id: InventoryColumnId,
  label: string,
  serverSortKey: string | undefined,
  groupCell: (row: InventoryGroupRow) => ReactNode,
): InventoryColumnDef {
  return {
    id,
    label,
    facets: ['order'],
    ...(serverSortKey === undefined ? {} : { serverSortKey }),
    cell: () => null,
    groupCell,
  }
}

function priceFor(row: InventoryListItem, target: StorefrontPrice['target']): StorefrontPrice {
  return (row.storefrontPrices ?? []).find((price) => price.target === target) ?? {
    target,
    connectionState: 'no_observation',
    listed: false,
    price: null,
    oldPrice: null,
    currency: null,
    listingState: null,
    observedAt: null,
    unavailableReason: 'listing.observation_absent',
  }
}

function readinessFor(row: InventoryListItem, target: StorefrontReadinessValue['target']): StorefrontReadinessValue {
  return (row.storefrontReadiness ?? []).find((readiness) => readiness.target === target) ?? {
    target,
    ready: false,
    issues: [{ code: 'readiness.unavailable', severity: 'blocking', label: 'Проверка недоступна' }],
  }
}

function pricePerGramFor(row: InventoryListItem, price: StorefrontPrice): number | null {
  const effectivePrice = price.target === 'ymarket' ? price.price : price.estimatedBuyerPrice ?? price.price
  return pricePerGramAmountFor(row, effectivePrice)
}

function pricePerGramAmountFor(row: InventoryListItem, amount: number | null): number | null {
  const grams = row.attributes.netWeightGrams
  return amount !== null && grams !== null && grams > 0 ? amount / grams : null
}

function pricePerGramReasonFor(row: InventoryListItem): string {
  return row.attributes.netWeightUnavailableReason ?? 'unit.weight_not_recorded'
}

function pricePerGramValue(row: InventoryListItem, amount: number) {
  const value = pricePerGramAmountFor(row, amount)
  return (
    <span className="mt-0.5 block text-[11px] tabular-nums text-ink-muted" data-price-role="per-gram">
      {value === null
        ? <ValueOrReason value={null} reason={pricePerGramReasonFor(row)} />
        : formatRublePerGram(value)}
    </span>
  )
}

function onecPriceCell(row: InventoryListItem) {
  if (row.onecPrice === null) return dash(null, row.onecPriceUnavailableReason)
  return (
    <span className="block tabular-nums">
      <span className="whitespace-nowrap">{formatRubleAmount(row.onecPrice)}</span>
      {pricePerGramValue(row, row.onecPrice)}
    </span>
  )
}

function storefrontPriceCell(row: InventoryListItem, target: StorefrontPrice['target']) {
  const price = priceFor(row, target)
  return (
    <StorefrontPriceCell
      price={price}
      readiness={readinessFor(row, target)}
      pricePerGram={price.price === null ? null : pricePerGramFor(row, price)}
      pricePerGramUnavailableReason={pricePerGramReasonFor(row)}
      drift={target === 'ymarket' ? ymarketDriftOf(row.ymarketEconomics?.prices) : null}
    />
  )
}

const OURS_WITHOUT_BUYER_PRICE = 'Цена для покупателя не собрана — показана наша цена'

/** A marketplace cell for WB and Ozon: the buyer-side price when Forge collected one, otherwise our price marked as ours. */
function buyerPriceCell(row: InventoryListItem, target: StorefrontPrice['target']) {
  const price = priceFor(row, target)
  const listed = price.price !== null && price.connectionState !== 'not_connected' && price.listed
  const buyer = listed ? price.estimatedBuyerPrice ?? null : null
  const amount = listed ? buyer ?? price.price : null
  return (
    <BuyerPriceCell
      amount={amount}
      unavailableReason={price.unavailableReason ?? 'price.not_provided'}
      basis={buyer === null ? 'ours' : 'buyer'}
      pricePerGram={pricePerGramAmountFor(row, amount)}
      pricePerGramUnavailableReason={pricePerGramReasonFor(row)}
      description={buyer === null ? OURS_WITHOUT_BUYER_PRICE : `Покупатель заплатит: ${formatRubleAmount(buyer)} · наша цена ${formatRubleAmount(price.price)}`}
    />
  )
}

/** «Цена Яндекс» is the price on the Market showcase without Yandex Pay and its ₽/g; the rest is in the tooltip. */
function ymarketPriceCell(row: InventoryListItem) {
  const figure = ymarketShowcaseFigure(row.ymarketEconomics, row.attributes.netWeightGrams, row.attributes.netWeightUnavailableReason)
  const drifted = row.ymarketEconomics?.syncCheck?.drifted === true
  const cell = (
    <BuyerPriceCell
      amount={figure.amount}
      unavailableReason={figure.unavailableReason}
      basis="buyer"
      pricePerGram={figure.perGram}
      pricePerGramUnavailableReason={figure.perGramUnavailableReason}
      description={figure.description}
    />
  )
  if (!drifted) return cell
  return (
    <span className="inline-flex items-start gap-1">
      {cell}
      <span role="img" aria-label="Заказ разошёлся с витриной больше чем на 2%" title={syncText(row.ymarketEconomics?.syncCheck) ?? undefined} className="text-[12px] font-semibold text-st-unpaid" data-price-role="sync-drift">≠</span>
    </span>
  )
}

const ECONOMICS_UNAVAILABLE = 'ymarket_economics_unavailable'

/** The showcase without Yandex Pay per gram of the card weight. */
function showcasePerGramCell(row: InventoryListItem) {
  const figure = ymarketShowcaseFigure(row.ymarketEconomics, row.attributes.netWeightGrams, row.attributes.netWeightUnavailableReason)
  if (figure.amount === null) return <ValueOrReason value={null} reason={figure.unavailableReason} />
  if (figure.perGram === null) return <ValueOrReason value={null} reason={figure.perGramUnavailableReason} />
  return <span className="whitespace-nowrap font-semibold tabular-nums text-ink" data-price-role="showcase-per-gram" title={figure.description}>{formatRublePerGram(figure.perGram)}</span>
}

/** Showcase ₽/г as a share of the piece's own cheapThreshold, so gold and silver rank together. */
function showcaseThresholdCell(row: InventoryListItem) {
  const figure = ymarketShowcaseFigure(row.ymarketEconomics, row.attributes.netWeightGrams, row.attributes.netWeightUnavailableReason)
  if (figure.amount === null) return <ValueOrReason value={null} reason={figure.unavailableReason} />
  if (figure.perGram === null) return <ValueOrReason value={null} reason={figure.perGramUnavailableReason} />
  const showcase = row.ymarketEconomics?.showcase ?? null
  const threshold = showcase?.cheapThreshold ?? null
  if (threshold === null || threshold <= 0) return <ValueOrReason value={null} reason={showcase?.cheapThresholdReason ?? 'cheap_threshold_not_computed'} />
  const percent = Math.round((figure.perGram / threshold) * 100)
  return <span className="whitespace-nowrap tabular-nums text-ink" data-price-role="showcase-threshold-ratio" title={`Витрина ${formatRublePerGram(figure.perGram)} при пороге ${formatRublePerGram(threshold)}`}>{`${percent} %`}</span>
}

/** A stored Market value, or «—» carrying the named reason; never a zero stand-in. */
function economicsCell(row: InventoryListItem, pick: (economics: NonNullable<InventoryListItem['ymarketEconomics']>) => { value: string | null; reason: string | null }) {
  const economics = row.ymarketEconomics
  if (economics === null || economics === undefined) return <ValueOrReason value={null} reason={ECONOMICS_UNAVAILABLE} />
  const { value, reason } = pick(economics)
  return value === null
    ? <ValueOrReason value={null} reason={reason ?? ECONOMICS_UNAVAILABLE} />
    : <span className="whitespace-nowrap tabular-nums">{value}</span>
}

function earningsPercentText(value: number): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`
}

function NetWalletGlyph() {
  return <svg aria-hidden="true" viewBox="0 0 16 16" className="size-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M2.2 4.3h10.6a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H3.2a1 1 0 0 1-1-1V3.7a1 1 0 0 1 1-1h8.7"/><path d="M10 8h3.8v2.5H10a1.2 1.2 0 0 1 0-2.5Z"/><circle cx="10.7" cy="9.25" r=".35" fill="currentColor"/></svg>
}

function netPerGramCell(row: InventoryListItem) {
  const economics = row.ymarketEconomics
  if (economics?.netPerGram === null || economics?.netPerGram === undefined) {
    return <ValueOrReason value={null} reason={economics?.netPerGramUnavailableReason ?? ECONOMICS_UNAVAILABLE} />
  }
  const basisText = economics.netPriceBasis === 'seller_promo_price' ? 'по цене нашей акции' : 'по нашей цене'
  return <span className="inline-flex items-center gap-1 whitespace-nowrap tabular-nums" title={`На руки по собранному тарифу Маркета, ${basisText}.`}><NetWalletGlyph />{formatRublePerGram(economics.netPerGram)}</span>
}

type MarketPrices = NonNullable<NonNullable<InventoryListItem['ymarketEconomics']>['prices']>

/** The collected Market prices of a row, or the named reason they are absent. */
function marketPricesOf(row: InventoryListItem): { prices: MarketPrices } | { reason: string } {
  const economics = row.ymarketEconomics
  if (economics === null || economics === undefined) return { reason: ECONOMICS_UNAVAILABLE }
  if (economics.prices === null) return { reason: ECONOMICS_UNAVAILABLE }
  return { prices: economics.prices }
}

function marketSideReason(prices: MarketPrices, absent: string): string {
  if (prices.driftState === 'never_collected') return 'ymarket_prices_never_collected'
  if (prices.driftState === 'missing_on_ym') return 'ymarket_offer_missing'
  return absent
}

function money(value: number | null, reason: string, struck = false) {
  if (value === null) return <ValueOrReason value={null} reason={reason} />
  return <span className={`whitespace-nowrap tabular-nums ${struck ? 'line-through' : ''}`}>{formatRubleAmount(value)}</span>
}

/** One Market price field, kept on its side: ours above, the Market's below; one «—» when neither is known. */
function attributedPrices(row: InventoryListItem, field: 'discountBase' | 'minimumForPromos') {
  const found = marketPricesOf(row)
  if ('reason' in found) return <ValueOrReason value={null} reason={found.reason} />
  const { prices } = found
  const struck = field === 'discountBase'
  const forge = field === 'discountBase' ? prices.forgeDiscountBase : prices.forgeMinimumForPromos
  const ym = field === 'discountBase' ? prices.ymDiscountBase : prices.ymMinimumForPromos
  const ymAbsent = field === 'discountBase' ? 'ymarket_discount_base_absent' : 'ymarket_minimum_absent'
  if (forge === null && ym === null) return <ValueOrReason value={null} reason={marketSideReason(prices, ymAbsent)} />
  return (
    <span className="flex flex-col gap-0.5 text-xs">
      <span data-side="forge"><span className="text-ink-muted">наша </span>{money(forge, 'forge_price_not_set', struck)}</span>
      <span data-side="ymarket"><span className="text-ink-muted">Маркет </span>{money(ym, marketSideReason(prices, ymAbsent), struck)}</span>
    </span>
  )
}

function driftCell(row: InventoryListItem) {
  const found = marketPricesOf(row)
  if ('reason' in found) return <ValueOrReason value={null} reason={found.reason} />
  const state = found.prices.driftState
  const tone = state === 'in_sync' ? 'text-received' : state === 'never_collected' ? 'text-ink-muted' : 'text-st-unpaid'
  return <span data-drift={state} className={`whitespace-nowrap text-xs font-medium ${tone}`}>{DRIFT_LABELS[state]}</span>
}

function freshnessCell(row: InventoryListItem) {
  const found = marketPricesOf(row)
  if ('reason' in found) return <ValueOrReason value={null} reason={found.reason} />
  const collectedAt = found.prices.collectedAt
  if (collectedAt === null) return <ValueOrReason value={null} reason="ymarket_prices_never_collected" />
  const stale = row.ymarketEconomics?.stale === true
  const moment = new Date(collectedAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' })
  return (
    <span className="flex flex-col text-xs" data-stale={stale ? 'true' : 'false'}>
      <time dateTime={collectedAt} className="tabular-nums">{moment}</time>
      {stale ? <span className="font-medium text-st-unpaid">устарели</span> : null}
    </span>
  )
}

export const INVENTORY_COLUMN_REGISTRY: readonly InventoryColumnDef[] = [
  { id: 'photo', label: 'Фото', facets: ['core'], defaultVisible: true, cell: (row) => <WarehouseThumbnail mediaUrl={row.primaryMediaUrl} unavailableReason={row.primaryMediaUnavailableReason} label={`Фото ${row.title ?? displayArticle(row)}`} size="large" /> },
  { id: 'title', label: 'Изделие', serverSortKey: 'title', facets: ['core'], defaultVisible: true, cell: title },
  { id: 'product', label: 'Изделие и идентификаторы', serverSortKey: 'article', facets: ['core'], cell: product },
  { id: 'attributes', label: 'Материал', facets: ['core'], defaultVisible: true, cell: (row) => <><ValueOrReason value={row.attributes.material === null ? null : materialLabel(row.attributes.material)} reason={row.attributes.materialUnavailableReason} /> · <ValueOrReason value={row.attributes.fineness} reason={row.attributes.finenessUnavailableReason} /></> },
  { id: 'size', label: 'Размер', facets: ['core'], defaultVisible: true, serverSortKey: 'size', cell: (row) => dash(row.techSize, 'identity.size_absent') },
  { id: 'weight', label: 'Вес', facets: ['core'], defaultVisible: true, serverSortKey: 'weight', cell: (row) => dash(row.attributes.netWeightGrams, row.attributes.netWeightUnavailableReason) },
  { id: 'metalWeight', label: 'Вес металла', facets: ['core'], cell: (row) => row.composition === null ? dash(null, 'metal_weight.not_extracted') : dash(row.composition.metalWeightGrams, row.composition.metalWeightUnavailableReason) },
  { id: 'insertWeight', label: 'Вес вставок', facets: ['core'], cell: insertWeightCell },
  { id: 'inserts', label: 'Вставки', facets: ['core'], cell: insertsCell },
  { id: 'article', label: 'Артикул', facets: ['core'], defaultVisible: true, serverSortKey: 'article', cell: (row) => <span className="flex flex-col font-semibold" data-reason={row.modelUnavailableReason ?? undefined} title={row.modelUnavailableReason ? unavailableReasonText(row.modelUnavailableReason) : undefined}><span>{displayArticle(row) || 'Без артикула'}</span></span> },
  { id: 'uin', label: 'УИН', facets: ['core'], defaultVisible: true, serverSortKey: 'uin', cell: (row) => dash(productUinOf(row), 'unit.marking_not_recorded_in_source') },
  { id: 'arrivedAt', label: 'Поступил', facets: ['core'], defaultVisible: true, cell: (row) => row.onecArrivedAt ? <time dateTime={row.onecArrivedAt}>{warehouseDateOnly(row.onecArrivedAt)}</time> : <ValueOrReason value={null} reason={row.onecArrivedAtUnavailableReason} /> },
  { id: 'marketplaces', label: 'Площадки', facets: ['core'], defaultVisible: true, cell: (row) => <MarketplaceBadgeList badges={marketplaceBadges(row, null)} compact /> },
  { id: 'storefronts', label: 'Витрины', facets: ['storefrontPrices', 'presence', 'readiness', 'ymarketEconomics'], cell: (row) => <StorefrontCompactCell row={row} /> },
  { id: 'status', label: 'Статус изделия', facets: ['core'], defaultVisible: true, cell: status },
  { id: 'activity', label: 'Движение', facets: ['core'], defaultVisible: true, serverSortKey: 'movement', cell: activity },
  { id: 'alerts', label: 'Алерты', facets: ['core'], defaultVisible: true, cell: (row) => <AlertsCell row={row} /> },
  { id: 'problems', label: 'Проблемы', facets: ['core'], defaultVisible: true, cell: (row) => <ProblemsCell problems={row.problems ?? []} problemsAvailable={row.problemsAvailable} /> },
  { id: 'photoCount', label: 'Фотографии', facets: ['core'], defaultVisible: true, serverSortKey: 'photoCount', cell: (row) => mediaCount(row.photoCount, 'data-photo-count') },
  // The product-grain InventoryListItem payload does not publish a video count.
  // Keep that unknown distinct from a measured zero; grouped rows have their
  // own count in InventoryGroupRow and render it in the grouped table.
  { id: 'videoCount', label: 'Видео', facets: ['core'], defaultVisible: true, cell: () => mediaCount(undefined, 'data-video-count') },
  { id: 'onecPrice', label: 'Цена 1С', facets: ['storefrontPrices'], serverSortKey: 'onecPrice', cell: onecPriceCell },
  { id: 'wildberriesPrice', label: 'Цена WB', facets: ['storefrontPrices', 'presence'], serverSortKey: 'storefrontPrice:wildberries', cell: (row) => buyerPriceCell(row, 'wildberries') },
  { id: 'ymarketPrice', label: 'Цена Яндекс', facets: ['storefrontPrices', 'presence', 'ymarketEconomics'], serverSortKey: 'storefrontPrice:ymarket', cell: ymarketPriceCell },
  { id: 'ymarketSellsFor', label: 'Продаётся за', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_sells_for', cell: (row) => economicsCell(row, (economics) => ({ value: economics.sellsFor === null ? null : formatRubleAmount(economics.sellsFor), reason: economics.sellsForReason })) },
  { id: 'ymarketShowcase', label: 'Покупатель заплатит', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_showcase', cell: (row) => row.ymarketEconomics === null || row.ymarketEconomics === undefined ? <ValueOrReason value={null} reason={ECONOMICS_UNAVAILABLE} /> : <MarketShowcasePrice showcase={row.ymarketEconomics.showcase} /> },
  { id: 'ymarketShowcasePerGram', label: 'Витрина, ₽/г', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_showcase_per_gram', cell: showcasePerGramCell },
  { id: 'ymarketShowcaseThresholdRatio', label: 'Витрина к порогу', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_showcase_threshold_ratio', cell: showcaseThresholdCell },
  { id: 'ymarketPromo', label: 'Акция', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_promo', cell: (row) => <MarketDriverIcon economics={row.ymarketEconomics} kind="promo" /> },
  { id: 'ymarketBoost', label: 'Буст', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_boost', cell: (row) => <MarketDriverIcon economics={row.ymarketEconomics} kind="boost" /> },
  { id: 'ymarketRubPerGram', label: '₽/г', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_price_per_gram', cell: (row) => economicsCell(row, (economics) => ({ value: economics.pricePerGram === null ? null : formatRublePerGram(economics.pricePerGram), reason: economics.weightGrams === null ? 'weight_not_measured' : economics.sellsForReason })) },
  { id: 'ymarketPurchaseCost', label: 'Закупка', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_purchase_cost', cell: (row) => economicsCell(row, (economics) => ({ value: economics.purchaseCost === null ? null : formatRubleAmount(economics.purchaseCost), reason: 'cost_not_known' })) },
  { id: 'ymarketEarnings', label: 'Заработок ₽', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_earnings', cell: (row) => economicsCell(row, (economics) => ({ value: economics.earnings.earnings === null ? null : formatRubleAmount(economics.earnings.earnings), reason: economics.earnings.unavailableReason })) },
  { id: 'ymarketEarningsPercent', label: 'Заработок %', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_earnings_percent', cell: (row) => economicsCell(row, (economics) => ({ value: economics.earnings.earningsPercent === null ? null : earningsPercentText(economics.earnings.earningsPercent), reason: economics.earnings.unavailableReason })) },
  { id: 'ymarketNetPerGram', label: 'На руки, ₽/г', facets: ['ymarketEconomics'], serverSortKey: 'ymarket_net_per_gram', cell: netPerGramCell },
  { id: 'ymarketOurPrice', label: YM_PRICE_LABELS.ourPrice, facets: ['ymarketEconomics'], cell: (row) => { const found = marketPricesOf(row); return 'reason' in found ? <ValueOrReason value={null} reason={found.reason} /> : money(found.prices.forgePrice, 'forge_price_not_set') } },
  { id: 'ymarketDiscountBase', label: 'Зачёркнутая Маркета', facets: ['ymarketEconomics'], cell: (row) => attributedPrices(row, 'discountBase') },
  { id: 'ymarketMinimumForPromos', label: 'Минимум для акций', facets: ['ymarketEconomics'], cell: (row) => attributedPrices(row, 'minimumForPromos') },
  { id: 'ymarketMarketPrice', label: YM_PRICE_LABELS.marketPrice, facets: ['ymarketEconomics'], cell: (row) => { const found = marketPricesOf(row); return 'reason' in found ? <ValueOrReason value={null} reason={found.reason} /> : money(found.prices.ymPrice, marketSideReason(found.prices, 'price_not_collected')) } },
  { id: 'ymarketDrift', label: 'Расхождение', facets: ['ymarketEconomics'], cell: driftCell },
  { id: 'ymarketFreshness', label: 'Цены собраны', facets: ['ymarketEconomics'], cell: freshnessCell },
  { id: 'ozonPrice', label: 'Цена Ozon', facets: ['storefrontPrices', 'presence'], serverSortKey: 'storefrontPrice:ozon', cell: (row) => buyerPriceCell(row, 'ozon') },
  { id: 'oriSitePrice', label: 'Цена ORI', facets: ['storefrontPrices', 'presence'], serverSortKey: 'storefrontPrice:ori_site', cell: (row) => storefrontPriceCell(row, 'ori_site') },
  orderColumn('orderNumber', 'Заказ', 'orderNumber', (row) => row.orderNumber ? <span className="font-semibold tabular-nums">№{row.orderNumber}</span> : groupText(null)),
  orderColumn('orderCreatedAt', 'Создан', 'orderCreatedAt', (row) => groupDate(row.orderCreatedAt)),
  // Marketplaces hand the name in whatever case the buyer typed it, so it
  // reaches us as "кокшаров Максим" as readily as "Кокшаров Максим". The row
  // opens the order, so the link stops the click it owns from reaching it.
  orderColumn('buyer', 'Покупатель', 'buyer', (row) => {
    if (!row.buyer) return groupText(null)
    const name = formatCustomerName(row.buyer)
    if (!row.customerId) return groupText(name)
    return <Link
      to={`/customers/${encodeURIComponent(row.customerId)}`}
      onClick={(event) => event.stopPropagation()}
      className="rounded px-1 py-0.5 underline decoration-dotted underline-offset-2 hover:bg-bone hover:decoration-solid focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
    >{name}</Link>
  }),
  orderColumn('orderStatus', 'Статус заказа', 'orderStatus', orderStatus),
  orderColumn('program', 'Программа', undefined, (row) => row.program ? <span className="rounded-full border border-rule px-2 py-1 text-[10px] font-semibold">{row.program}</span> : groupText(null)),
  orderColumn('city', 'Город', 'city', (row) => groupText(row.city)),
  orderColumn('orderAmount', 'Сумма', 'orderAmount', (row) => <span className="whitespace-nowrap font-semibold tabular-nums">{formatRubleAmount(row.orderAmount)}</span>),
  orderColumn('composition', 'Состав', undefined, orderComposition),
]

export const WAREHOUSE_DEFAULT_VISIBLE_COLUMNS: readonly InventoryColumnId[] = [
  'photo',
  'title',
  'attributes',
  'size',
  'weight',
  'article',
  'uin',
  'arrivedAt',
  'status',
  'activity',
  'alerts',
  'problems',
  'photoCount',
  'videoCount',
  'marketplaces',
]

export const ORDER_GROUP_DEFAULT_VISIBLE_COLUMNS: readonly InventoryColumnId[] = [
  'orderNumber', 'orderCreatedAt', 'buyer', 'orderStatus', 'program', 'city', 'orderAmount', 'composition',
]
